import React, { useState } from 'react';

import { Box, Flex, FlexProps, MultiSelect, Select, Space, TextInput } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import popupMessagesPreview from 'assets/images/popup-messages-preview.png';
import { InputLabelWithInfo } from 'components/InputLabelWithInfo';
import { BubblePopupMessagesInput } from 'components/SmartAssistantSections/WebsiteIntegrationSection/BubblePopupMessagesInput';
import { WebsiteIntegrationPreviewCarousel } from 'components/SmartAssistantSections/WebsiteIntegrationSection/WebsiteIntegrationPreviewCarousel';
import { SmartAssistantWebsiteCodeSnipet } from 'components/SmartAssistantWebsiteCodeSnipet';
import { UpgradePlanBlurCard } from 'components/UpgradePlanBlurCard';
import { useSubscriptionContext } from 'contexts/SubscriptionContext';
import { useTranslation } from 'react-i18next';
import { MAX_WIDTH } from 'shared/constants/theme';
import {
  SmartAssistantBubbleAlignment,
  SmartAssistantVisualConfig,
  WebsiteConfigurationFormValues,
  WhiteLabelIntegration
} from 'shared/interfaces/SmartAssistant';
import { validateUrl } from 'shared/utils/form-validators';

interface WebsiteIntegrationSectionProps extends FlexProps {
  form: UseFormReturnType<WebsiteConfigurationFormValues>;
  smartAssistantVisualConfig: SmartAssistantVisualConfig;
  smartAssistantId?: string;
  whiteLabelIntegration?: WhiteLabelIntegration;
}

export function WebsiteIntegrationSection({
  form,
  smartAssistantVisualConfig,
  smartAssistantId,
  whiteLabelIntegration,
  ...flexProps
}: WebsiteIntegrationSectionProps) {
  const { t } = useTranslation();
  const [subscription] = useSubscriptionContext();

  const [inputCreateDomainValue, setInputCreateDomainValue] = useState('');

  // TODO: Decouple website integration from smartAssistantIntegrations within product feature flags
  const canIntegrate = subscription.product.smartAssistantIntegrations;

  return (
    <UpgradePlanBlurCard
      message={t('SMART_ASSISTANT.WEBSITE_INTEGRATION_SECTION.UPGRADE.DESCRIPTION')}
      showCard={!canIntegrate}>
      <Flex
        w="100%"
        direction={{ base: 'column', lg: 'row' }}
        align="flex-start"
        justify="space-between"
        gap="xl"
        maw={MAX_WIDTH}
        {...flexProps}>
        <Box maw={500} w="100%" {...flexProps}>
          <MultiSelect
            disabled={!canIntegrate}
            label={t('SMART_ASSISTANT.WEBSITE.ALLOWED_DOMAINS.LABEL')}
            data={form.values.allowedDomains.map((domain) => ({ value: domain, label: domain }))}
            placeholder={t('SMART_ASSISTANT.WEBSITE.ALLOWED_DOMAINS.PLACEHOLDER')}
            searchable
            creatable
            error={form.errors.allowedDomains}
            value={form.values.allowedDomains}
            onChange={(value) => form.setFieldValue('allowedDomains', value)}
            getCreateLabel={(query) => `${t('SMART_ASSISTANT.WEBSITE.ALLOWED_DOMAINS.ADD_DOMAIN')} ${query}`}
            searchValue={inputCreateDomainValue}
            onSearchChange={(value) => setInputCreateDomainValue(value)}
            onCreate={(query) => {
              const validation = validateUrl(query);
              if (validation) {
                form.setFieldError('allowedDomains', validation);
                return;
              }

              form.setFieldValue('allowedDomains', [...form.values.allowedDomains, query]);
              return { value: query, label: query };
            }}
            onKeyUp={(event) => {
              if (event.nativeEvent.code === 'Enter') {
                event.preventDefault();
                form.setFieldValue('allowedDomains', [...form.values.allowedDomains, inputCreateDomainValue]);
                setInputCreateDomainValue('');
              }
            }}
          />

          <Space h="md" />

          <Select
            label={
              <InputLabelWithInfo
                label={t('SMART_ASSISTANT.WEBSITE.AS_BUBBLE.ALIGNMENT.LABEL')}
                infoContentTranslationKey="SMART_ASSISTANT.WEBSITE.AS_BUBBLE.ALIGNMENT.INFO"
              />
            }
            placeholder={t('SMART_ASSISTANT.CONFIGURATION_SECTION.MODEL.PLACEHOLDER')}
            data={[
              {
                value: SmartAssistantBubbleAlignment.LEFT,
                label: t('SMART_ASSISTANT.WEBSITE.AS_BUBBLE.ALIGNMENT.LEFT')
              },
              {
                value: SmartAssistantBubbleAlignment.RIGHT,
                label: t('SMART_ASSISTANT.WEBSITE.AS_BUBBLE.ALIGNMENT.RIGHT')
              }
            ]}
            {...form.getInputProps('bubbleAlignment')}
          />

          <Space h="md" />

          <TextInput
            w="100%"
            label={t('SMART_ASSISTANT.WEBSITE.WELCOME_HEADER.TITLE.LABEL')}
            placeholder={t('SMART_ASSISTANT.WEBSITE.WELCOME_HEADER.TITLE.PLACEHOLDER')}
            {...form.getInputProps('welcomeHeaderTitle')}
          />

          <Space h="md" />

          <TextInput
            w="100%"
            label={t('SMART_ASSISTANT.WEBSITE.WELCOME_HEADER.SUBTITLE.LABEL')}
            placeholder={t('SMART_ASSISTANT.WEBSITE.WELCOME_HEADER.SUBTITLE.PLACEHOLDER')}
            {...form.getInputProps('welcomeHeaderSubtitle')}
          />

          <Space h="md" />

          <BubblePopupMessagesInput
            label={
              <InputLabelWithInfo
                label={t('SMART_ASSISTANT.WEBSITE.AS_BUBBLE.POPUP_MESSAGES.LABEL')}
                infoContentTranslationKey={t('SMART_ASSISTANT.WEBSITE.AS_BUBBLE.POPUP_MESSAGES.INFO')}
                image={popupMessagesPreview}
              />
            }
            placeholder={t('SMART_ASSISTANT.WEBSITE.AS_BUBBLE.POPUP_MESSAGES.PLACEHOLDER')}
            {...form.getInputProps('bubblePopupMessages')}
            value={form.values.bubblePopupMessages}
            onChange={(value) => form.setFieldValue('bubblePopupMessages', value)}
          />

          {smartAssistantId && (
            <>
              <Space h="md" />
              <SmartAssistantWebsiteCodeSnipet
                smartAssistantId={smartAssistantId ?? ''}
                whiteLabelIntegration={whiteLabelIntegration}
              />
            </>
          )}
        </Box>
        <WebsiteIntegrationPreviewCarousel
          maw={800}
          smartAssistantVisualConfig={smartAssistantVisualConfig}
          websiteIntegrationForm={form}
        />
      </Flex>
    </UpgradePlanBlurCard>
  );
}
